/**
 * El panel de la gestoría — `OD-0063` D-06 —, lo que no es pantalla: el orden,
 * los filtros, la caché de cinco minutos y el ZIP de «Descargar todos».
 *
 * ```text
 * ORDEN      primero las que tienen algo RECHAZADO por la AEAT; despues por
 *            nombre. Una fila sin datos (sin acceso, o que no lleva quien
 *            mira) no tiene rechazos que contar: va con las demas, por nombre
 * FILTROS    "con rechazos", "sin descargar este trimestre", "de <persona>".
 *            Los dos primeros solo casan filas CON datos: de una empresa en
 *            gris no se sabe nada, y decir que "no se ha descargado" seria
 *            afirmar algo que no se ha leido
 * CACHE      cinco minutos, y no se refresca sola (D-06 COSTE: 80 clientes son
 *            80 lecturas de D1). Vive en sessionStorage, que es de ESTA
 *            pestana y se borra al cerrarla, para que entrar en un cliente y
 *            volver -que recarga la pagina- no vuelva a leer los 80. Va a
 *            nombre de la gestoria y de la persona: otra sesion en la misma
 *            pestana no ve lo de la anterior
 * EL ZIP     uno por tanda de hasta 100, armado en el navegador con el mismo
 *            escritor que el paquete de PDF de la API. Si una empresa falla,
 *            el ZIP lleva un LEEME con cual y por que
 * ```
 *
 * Aquí no se decide nada fiscal: las cifras, el trimestre y quién puede
 * descargar los da la API en cada petición.
 */

import { construirZip, type EntradaZip } from "../../../api/src/modules/accountant-export/domain/zip";
import type { FilaDelPanel, PersonaDeLaFila, ResumenDeCliente } from "../api/cliente";

/**
 * Lo que se sabe de una fila.
 *
 * ```text
 * sin-acceso    fuera de Business: en gris, con el motivo, y NINGUN dato
 * no-la-llevas  quien gestiona la ve, pero el resumen es de quien la lleva
 * ```
 */
export type EstadoDeFila =
  | { readonly tipo: "sin-acceso" }
  | { readonly tipo: "no-la-llevas" }
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error"; readonly estado: number }
  | { readonly tipo: "datos"; readonly resumen: ResumenDeCliente };

/** Si se pide el resumen de esta fila: solo en Business y a quien la lleva. */
export function estadoInicial(fila: FilaDelPanel): EstadoDeFila {
  if (!fila.enBusiness) return { tipo: "sin-acceso" };
  if (!fila.llevasTu) return { tipo: "no-la-llevas" };
  return { tipo: "cargando" };
}

const rechazados = (e: EstadoDeFila | undefined): number => (e?.tipo === "datos" ? e.resumen.aeat.rechazados : 0);

const nombreDe = (f: FilaDelPanel): string => f.nombre ?? "";

/** D-06 ORDEN. No cambia la lista que recibe. */
export function ordenarFilas(
  filas: readonly FilaDelPanel[],
  estados: ReadonlyMap<string, EstadoDeFila>,
): readonly FilaDelPanel[] {
  return [...filas].sort((a, b) => {
    const ra = rechazados(estados.get(a.organizationId)) > 0;
    const rb = rechazados(estados.get(b.organizationId)) > 0;
    if (ra !== rb) return ra ? -1 : 1;
    return nombreDe(a).localeCompare(nombreDe(b), "es");
  });
}

export type FiltrosDelPanel = {
  readonly conRechazos: boolean;
  readonly sinDescargar: boolean;
  /** `userId` de la persona, o `null` para todas. */
  readonly persona: string | null;
};

export const SIN_FILTROS: FiltrosDelPanel = { conRechazos: false, sinDescargar: false, persona: null };

/** D-06 FILTROS, todos a la vez. */
export function filtrarFilas(
  filas: readonly FilaDelPanel[],
  estados: ReadonlyMap<string, EstadoDeFila>,
  filtros: FiltrosDelPanel,
): readonly FilaDelPanel[] {
  return filas.filter((f) => {
    const e = estados.get(f.organizationId);
    if (filtros.conRechazos && rechazados(e) === 0) return false;
    if (filtros.sinDescargar && !(e?.tipo === "datos" && e.resumen.ultimaDescarga === null)) return false;
    if (filtros.persona !== null && !f.personas.some((p) => p.userId === filtros.persona)) return false;
    return true;
  });
}

/** Cómo se nombra a una persona: su correo, o su id si ya no está (como en Configuración > Asesoría). */
export const nombreDePersona = (p: PersonaDeLaFila): string => p.email ?? p.userId;

/** Las personas del filtro «de <persona>»: cada una una vez, por correo. */
export function personasDelPanel(filas: readonly FilaDelPanel[]): readonly PersonaDeLaFila[] {
  const porId = new Map<string, PersonaDeLaFila>();
  for (const f of filas) for (const p of f.personas) porId.set(p.userId, p);
  return [...porId.values()].sort((a, b) => nombreDePersona(a).localeCompare(nombreDePersona(b), "es"));
}

/**
 * Quién hizo la última descarga, sin enseñar a quien no es de la gestoría:
 * la empresa también se descarga lo suyo, y el panel no es el sitio donde la
 * gestoría conoce los correos del equipo de su cliente.
 */
export type QuienDescargo =
  | { readonly tipo: "tu" }
  | { readonly tipo: "persona"; readonly email: string }
  | { readonly tipo: "la-empresa" };

export function quienDescargo(
  ultima: NonNullable<ResumenDeCliente["ultimaDescarga"]>,
  yo: string,
  personas: readonly PersonaDeLaFila[],
): QuienDescargo {
  if (ultima.userId === yo) return { tipo: "tu" };
  const p = personas.find((x) => x.userId === ultima.userId);
  return p === undefined ? { tipo: "la-empresa" } : { tipo: "persona", email: nombreDePersona(p) };
}

/**
 * La descarga que se acaba de hacer, sin volver a leer el shard: la API ya la
 * ha dejado en su auditoría, y esta es la fila que leería.
 */
export function conDescargaHecha(r: ResumenDeCliente, yo: string, formato: string, ahora: Date): ResumenDeCliente {
  return { ...r, ultimaDescarga: { en: ahora.toISOString(), userId: yo, formato } };
}

// ---------------------------------------------------------------- la caché

export const DURACION_DE_LA_CACHE_MS = 5 * 60 * 1000;
const CLAVE = "faktelio.panel-asesoria";

/** Lo justo de `Storage`: así se prueba sin navegador. */
export type Almacen = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/**
 * El almacén de ESTA pestaña, o `null` si el navegador no lo deja (una
 * ventana privada estricta, o el almacenamiento bloqueado). Sin él no hay
 * caché ni vuelta recordada, y todo lo demás funciona igual.
 */
export function almacenDeLaPestana(): Almacen | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

type Guardado = {
  readonly dueno: string;
  readonly entradas: Record<string, { readonly en: number; readonly resumen: ResumenDeCliente }>;
};

const claveDe = (organizationId: string, trimestre: string): string => `${organizationId}|${trimestre}`;

function leer(almacen: Almacen | null, dueno: string): Guardado {
  const vacio: Guardado = { dueno, entradas: {} };
  if (almacen === null) return vacio;
  try {
    const g = JSON.parse(almacen.getItem(CLAVE) ?? "null") as Guardado | null;
    return g !== null && g.dueno === dueno && typeof g.entradas === "object" && g.entradas !== null ? g : vacio;
  } catch {
    return vacio;
  }
}

function escribir(almacen: Almacen | null, g: Guardado): void {
  if (almacen === null) return;
  try {
    almacen.setItem(CLAVE, JSON.stringify(g));
  } catch {
    // Lleno o bloqueado: sin caché se vuelve a pedir, que es lo correcto.
  }
}

/** Lo de hace menos de cinco minutos, o `null`. Lo caducado se tira al leer. */
export function resumenEnCache(
  almacen: Almacen | null,
  dueno: string,
  organizationId: string,
  trimestre: string,
  ahora: number,
): ResumenDeCliente | null {
  const g = leer(almacen, dueno);
  const vivas = Object.fromEntries(Object.entries(g.entradas).filter(([, e]) => ahora - e.en < DURACION_DE_LA_CACHE_MS && e.en <= ahora));
  if (Object.keys(vivas).length !== Object.keys(g.entradas).length) escribir(almacen, { dueno, entradas: vivas });
  return vivas[claveDe(organizationId, trimestre)]?.resumen ?? null;
}

/**
 * Guarda una fila. Con `conservarHora`, una fila que ya estaba no rejuvenece:
 * apuntar una descarga no alarga los cinco minutos de lo demás que trae.
 */
export function guardarResumen(
  almacen: Almacen | null,
  dueno: string,
  trimestre: string,
  resumen: ResumenDeCliente,
  ahora: number,
  conservarHora = false,
): void {
  const g = leer(almacen, dueno);
  const clave = claveDe(resumen.organizationId, trimestre);
  const en = conservarHora ? (g.entradas[clave]?.en ?? ahora) : ahora;
  escribir(almacen, { dueno, entradas: { ...g.entradas, [clave]: { en, resumen } } });
}

/** «Actualizar»: se tira todo lo de esta gestoría y esta persona. */
export function vaciarCache(almacen: Almacen | null): void {
  try {
    almacen?.removeItem(CLAVE);
  } catch {
    // Nada que tirar.
  }
}

// ------------------------------------------------- entrar y volver (D-05)

const VUELTA = "faktelio.vuelta-al-panel";

/**
 * «Entrar» en un cliente cambia la empresa activa de la SESIÓN, y la página se
 * recarga entera. Lo que se recuerda, en esta pestaña, es a qué gestoría
 * volver y que al volver se abra el panel. No da acceso a nada: volver es el
 * mismo cambio de empresa, con sus dos fases en la API.
 */
export function recordarVuelta(almacen: Almacen | null, gestoria: string): void {
  try {
    almacen?.setItem(VUELTA, JSON.stringify({ gestoria, abrirPanel: false }));
  } catch {
    // Sin almacen, «Volver al panel» pregunta a que empresa.
  }
}

/** La gestoría de la que se vino, si se vino de una. */
export function vueltaRecordada(almacen: Almacen | null): string | null {
  try {
    const v = JSON.parse(almacen?.getItem(VUELTA) ?? "null") as { gestoria?: unknown } | null;
    return typeof v?.gestoria === "string" ? v.gestoria : null;
  } catch {
    return null;
  }
}

/** Al volver: se olvida la gestoría y se pide abrir el panel UNA vez. */
export function volverAlPanel(almacen: Almacen | null): void {
  try {
    almacen?.setItem(VUELTA, JSON.stringify({ abrirPanel: true }));
  } catch {
    // Se vuelve igual, al Inicio.
  }
}

/** Si hay que abrir el panel al cargar. Se gasta al leerlo. */
export function abrirPanelPendiente(almacen: Almacen | null): boolean {
  try {
    const v = JSON.parse(almacen?.getItem(VUELTA) ?? "null") as { abrirPanel?: unknown } | null;
    if (v?.abrirPanel !== true) return false;
    almacen?.removeItem(VUELTA);
    return true;
  } catch {
    return false;
  }
}

// ------------------------------------------------------- descargar todos

export const POR_TANDA = 100;

/** D-06 DESCARGAR TODOS: «hasta 100 clientes por tanda». */
export function tandas<T>(lista: readonly T[], tamano = POR_TANDA): readonly (readonly T[])[] {
  const salida: T[][] = [];
  for (let i = 0; i < lista.length; i += tamano) salida.push(lista.slice(i, i + tamano));
  return salida;
}

/**
 * ASCII imprimible y sin separadores: lo que admite el escritor del ZIP. Las
 * tildes se quitan (Peñas -> Penas) en vez de perder la letra.
 */
function aAscii(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/**
 * El nombre de cada Excel dentro del ZIP: NIF, empresa y trimestre, para que
 * se ordene solo y se reconozca sin abrirlo. Dos empresas que dieran el mismo
 * nombre llevan `-2`, `-3`: el ZIP no admite repetidos.
 */
export function nombresEnElZip(
  empresas: ReadonlyArray<{ readonly nombre: string | null; readonly nif: string | null }>,
  trimestre: string,
): readonly string[] {
  const vistos = new Map<string, number>();
  return empresas.map((e) => {
    const base = [e.nif === null ? "" : aAscii(e.nif), aAscii(e.nombre ?? ""), aAscii(trimestre)].filter((x) => x !== "").join("_") || "empresa";
    const n = (vistos.get(base) ?? 0) + 1;
    vistos.set(base, n);
    return `${n === 1 ? base : `${base}-${n}`}.xlsx`;
  });
}

export type DescargaDeLaTanda =
  | { readonly ok: true; readonly nombre: string | null; readonly nif: string | null; readonly bytes: Uint8Array }
  | { readonly ok: false; readonly nombre: string | null; readonly nif: string | null; readonly motivo: string };

/**
 * El ZIP de una tanda. Los que fallaron no llevan fichero: van en `LEEME.txt`,
 * con su motivo, para que la gestoría sepa cuáles le faltan sin compararlo a
 * mano con su lista. `textos` lo pone la pantalla (los textos viven en i18n).
 */
export function zipDeLaTanda(
  descargas: readonly DescargaDeLaTanda[],
  trimestre: string,
  dia: string,
  textos: { readonly leeme: (fallos: ReadonlyArray<{ readonly empresa: string; readonly motivo: string }>) => string; readonly sinNombre: string },
): Uint8Array {
  const buenas = descargas.filter((d): d is Extract<DescargaDeLaTanda, { ok: true }> => d.ok);
  const nombres = nombresEnElZip(buenas, trimestre);
  const entradas: EntradaZip[] = buenas.map((d, i) => ({ nombre: nombres[i] as string, bytes: d.bytes }));
  const fallos = descargas.filter((d): d is Extract<DescargaDeLaTanda, { ok: false }> => !d.ok);
  if (fallos.length > 0) {
    const lineas = fallos.map((f) => ({
      empresa: f.nif === null ? (f.nombre ?? textos.sinNombre) : `${f.nombre ?? textos.sinNombre} (${f.nif})`,
      motivo: f.motivo,
    }));
    entradas.push({ nombre: "LEEME.txt", bytes: new TextEncoder().encode(textos.leeme(lineas)) });
  }
  return construirZip(entradas, dia);
}
